import { z } from 'zod';
import {
  BID_PACKAGE_STATUS_LABEL,
  BidPackageSchema,
  BidSchema,
  canTransitionPackage,
  isPackageOpenForBids,
  isPackageVisibleToTrades,
  type Bid,
  type BidPackage,
  type BidPackageStatus,
} from '../../domain/entities/bid.ts';
import { MoneySchema, type Money } from '../../domain/money.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged, updateWrite } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Query, Unsubscribe } from '../../ports/query.ts';
import type { FunctionsPort } from '../../ports/functions-port.ts';
import { createPurchaseOrder } from '../purchase-orders/save-purchase-order.ts';

/**
 * Bid packages, and the bids against them (SCRUM-143, P8-10).
 *
 * **A bid is a document under its package**, not a field on it, and that
 * is the single most important decision in this module. Firestore rules
 * are all-or-nothing per document: prices kept on the package would be in
 * the bytes every invited trade received, whatever the screen drew. A
 * sibling document can carry its own rule, which is how one trade is kept
 * from reading another's price.
 */
export class BidError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BidError';
  }
}

export interface BidContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

const scope = (context: BidContext) => ({
  companyId: context.companyId,
  jobId: context.jobId,
});

const packages = (context: BidContext) =>
  context.data.collection<BidPackage>(scope(context), 'bidPackages');

/** The bids under one package, which is a collection of its own. */
const bidsIn = (context: BidContext, packageId: string) =>
  context.data.collection<Bid>(
    { ...scope(context), record: { collection: 'bidPackages', id: packageId } },
    'bids',
  );

export async function listBidPackages(context: BidContext): Promise<BidPackage[]> {
  const page = await packages(context).list();
  // Newest first: a bid package list is read to find what is out now.
  return [...page.items].sort(
    (a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id),
  );
}

export function subscribeToBidPackages(
  context: BidContext,
  onChange: (packages: readonly BidPackage[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return packages(context).subscribe(
    {},
    (page) =>
      onChange(
        [...page.items].sort(
          (a, b) => b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id),
        ),
      ),
    onError,
  );
}

export async function getBidPackage(context: BidContext, id: string): Promise<BidPackage> {
  const found = await packages(context).get(id);
  if (!found) throw new NotFoundError('bidPackages', id);
  return found;
}

/**
 * Every bid on one package — the office's comparison.
 *
 * An unfiltered query on the package's own subcollection, which is why
 * bids live there: reading one package's bids needs no composite index
 * and no filter a rule has to trust. The same reasoning put a change
 * order's log under the change order.
 */
export async function listBids(context: BidContext, packageId: string): Promise<Bid[]> {
  const page = await bidsIn(context, packageId).list();
  return [...page.items].sort((a, b) => a.amount.amount - b.amount.amount);
}

/** The one query a trade may issue against a package's bids. */
export function ownBidQuery(contactId: string): Query {
  return { where: [{ field: 'contactId', op: '==', value: contactId }] };
}

export async function myBid(
  context: BidContext,
  packageId: string,
  contactId: string,
): Promise<Bid | null> {
  const page = await bidsIn(context, packageId).list(ownBidQuery(contactId));
  return page.items[0] ?? null;
}

export const PackageInputSchema = z.object({
  title: z.string().trim().min(1, 'A package needs a title').max(200),
  scope: z.string().trim().max(20_000).default(''),
  costCodeId: z.string().optional(),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'A due date is a calendar date')
    .optional(),
});
export type PackageInput = z.input<typeof PackageInputSchema>;

/**
 * Write a package.
 *
 * It starts as a **draft**, invisible to every trade. A builder writes a
 * scope over a morning, and a package that went out the moment it was
 * created would put half a scope in front of four of them.
 */
export async function createBidPackage(
  context: BidContext,
  input: PackageInput,
): Promise<BidPackage> {
  const parsed = PackageInputSchema.safeParse(input);
  if (!parsed.success) throw new BidError(parsed.error.issues[0]!.message);

  const at = context.clock.now().toISOString();
  const pkg = BidPackageSchema.parse({
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    title: parsed.data.title,
    scope: parsed.data.scope,
    ...(parsed.data.costCodeId ? { costCodeId: parsed.data.costCodeId } : {}),
    ...(parsed.data.dueDate ? { dueDate: parsed.data.dueDate } : {}),
    status: 'draft',
    invitedContactIds: [],
    createdAt: at,
    createdBy: context.uid,
    updatedAt: at,
    updatedBy: context.uid,
    version: 1,
  });
  await packages(context).create(pkg);
  return pkg;
}

async function writePackage(
  context: BidContext,
  existing: BidPackage,
  patch: Partial<BidPackage>,
): Promise<BidPackage> {
  const updatedAt = context.clock.now().toISOString();
  await context.data.commit([
    updateWrite<BidPackage>(
      scope(context),
      'bidPackages',
      existing.id,
      { ...patch, updatedAt, updatedBy: context.uid },
      expectUnchanged(existing),
    ),
  ]);
  return { ...existing, ...patch, updatedAt, updatedBy: context.uid };
}

export async function editBidPackage(
  context: BidContext,
  pkg: BidPackage,
  input: PackageInput,
): Promise<BidPackage> {
  const parsed = PackageInputSchema.safeParse(input);
  if (!parsed.success) throw new BidError(parsed.error.issues[0]!.message);
  return writePackage(context, pkg, {
    title: parsed.data.title,
    scope: parsed.data.scope,
    costCodeId: parsed.data.costCodeId ?? undefined,
    dueDate: parsed.data.dueDate ?? undefined,
  });
}

/**
 * Ask a trade to price it.
 *
 * **Invitations are kept even when nobody answers**, which is the reason
 * they live on the package rather than being inferred from the bids:
 * "nobody else was asked" and "three others were asked and said nothing"
 * are different facts, and only the second tells a builder their scope is
 * the problem.
 */
export async function inviteToBid(
  context: BidContext,
  pkg: BidPackage,
  contactId: string,
): Promise<BidPackage> {
  if (pkg.status === 'awarded') {
    throw new BidError(`${pkg.title} has been awarded. There is nothing left to price.`);
  }
  if (pkg.invitedContactIds.includes(contactId)) {
    throw new BidError('They have already been asked to price this.');
  }
  return writePackage(context, pkg, {
    invitedContactIds: [...pkg.invitedContactIds, contactId],
  });
}

/**
 * Take an invitation back.
 *
 * **Refused once they have priced it.** Removing the invitation would
 * leave a bid from somebody the package says was never asked, which is
 * the sort of record that makes a dispute worse rather than better.
 */
export async function uninviteFromBid(
  context: BidContext,
  pkg: BidPackage,
  contactId: string,
  bids: readonly Bid[],
): Promise<BidPackage> {
  if (bids.some((bid) => bid.contactId === contactId)) {
    throw new BidError(
      'They have already priced this. Their bid would be left with no invitation.',
    );
  }
  return writePackage(context, pkg, {
    invitedContactIds: pkg.invitedContactIds.filter((one) => one !== contactId),
  });
}

/**
 * Move a package through its lifecycle.
 *
 * **Sending one out with nobody invited is refused.** A package "out to
 * bid" that nobody was asked to price reads, a fortnight later, as four
 * trades who ignored it.
 */
export async function transitionBidPackage(
  context: BidContext,
  pkg: BidPackage,
  to: BidPackageStatus,
): Promise<BidPackage> {
  if (!canTransitionPackage(pkg.status, to)) {
    throw new BidError(
      `A package that is ${BID_PACKAGE_STATUS_LABEL[pkg.status].toLowerCase()} cannot become ${BID_PACKAGE_STATUS_LABEL[to].toLowerCase()}.`,
    );
  }
  if (to === 'out' && pkg.invitedContactIds.length === 0) {
    throw new BidError('Ask somebody to price it first, or it goes out to nobody.');
  }
  return writePackage(context, pkg, { status: to });
}

export const BidInputSchema = z.object({
  amount: MoneySchema,
  notes: z.string().trim().max(20_000).default(''),
});
export type BidInput = z.input<typeof BidInputSchema>;

/**
 * Submit a price, or replace the one already given.
 *
 * **One bid per trade per package**, which is why this is an upsert
 * rather than a create: a trade who sends a second price has corrected
 * the first, and keeping both would make the comparison a question about
 * which is current.
 *
 * Refused unless the package is **out**. A price given against a draft
 * is a price for a scope nobody has published, and one given after the
 * close is one the others did not get to answer.
 */
export async function submitBid(
  context: BidContext,
  pkg: BidPackage,
  contactId: string,
  input: BidInput,
): Promise<Bid> {
  const parsed = BidInputSchema.safeParse(input);
  if (!parsed.success) throw new BidError(parsed.error.issues[0]!.message);
  if (parsed.data.amount.amount <= 0) {
    throw new BidError('A bid needs a price.');
  }
  if (!isPackageOpenForBids(pkg)) {
    throw new BidError(
      `${pkg.title} is ${BID_PACKAGE_STATUS_LABEL[pkg.status].toLowerCase()} and is not taking prices.`,
    );
  }
  if (!pkg.invitedContactIds.includes(contactId)) {
    throw new BidError('They were not asked to price this.');
  }

  const at = context.clock.now().toISOString();
  const existing = await myBid(context, pkg.id, contactId);
  if (existing) {
    const patch = {
      amount: parsed.data.amount as Money,
      notes: parsed.data.notes,
      submittedAt: at,
      withdrawn: false,
    };
    await context.data.commit([
      updateWrite<Bid>(
        { ...scope(context), record: { collection: 'bidPackages', id: pkg.id } },
        'bids',
        existing.id,
        { ...patch, updatedAt: at, updatedBy: context.uid },
        expectUnchanged(existing),
      ),
    ]);
    return { ...existing, ...patch, updatedAt: at, updatedBy: context.uid };
  }

  const bid = BidSchema.parse({
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    packageId: pkg.id,
    contactId,
    amount: parsed.data.amount,
    notes: parsed.data.notes,
    submittedAt: at,
    withdrawn: false,
    createdAt: at,
    createdBy: context.uid,
    updatedAt: at,
    updatedBy: context.uid,
    version: 1,
  });
  await bidsIn(context, pkg.id).create(bid);
  return bid;
}

/**
 * Pull a price back.
 *
 * Marked rather than removed: a trade who withdraws has still been asked
 * and still answered, and a builder comparing four bids needs to know the
 * fourth existed.
 */
export async function withdrawBid(context: BidContext, bid: Bid): Promise<Bid> {
  if (bid.withdrawn) throw new BidError('That bid has already been withdrawn.');
  const at = context.clock.now().toISOString();
  await context.data.commit([
    updateWrite<Bid>(
      { ...scope(context), record: { collection: 'bidPackages', id: bid.packageId } },
      'bids',
      bid.id,
      { withdrawn: true, updatedAt: at, updatedBy: context.uid },
      expectUnchanged(bid),
    ),
  ]);
  return { ...bid, withdrawn: true, updatedAt: at, updatedBy: context.uid };
}

/**
 * Accept one.
 *
 * Two writes in one commit — the package becomes `awarded` and records
 * which bid won — so a package can never be awarded to a bid it does not
 * name, or name one without being awarded. The purchase order this should
 * create is P8-10d; doing it here would make awarding depend on a
 * callable and leave the award half-done when the callable failed.
 */
export async function awardBid(
  context: BidContext,
  pkg: BidPackage,
  bid: Bid,
): Promise<BidPackage> {
  if (pkg.status !== 'closed') {
    throw new BidError('Close the package first, so nobody is still pricing it.');
  }
  if (bid.packageId !== pkg.id) throw new BidError('That bid is on another package.');
  if (bid.withdrawn) throw new BidError('That bid was withdrawn.');
  return writePackage(context, pkg, { status: 'awarded', awardedBidId: bid.id });
}

/**
 * Turn an award into a purchase order (P8-10d).
 *
 * **Two steps, in this order, and the order is the whole design.** The
 * award is already recorded by `awardBid`; this raises the numbered
 * instrument and then writes its id back onto the package. Doing it the
 * other way — or doing both inside `awardBid` — would make the builder's
 * decision depend on a callable succeeding, and a failed call would
 * leave a package neither awarded nor not.
 *
 * **The caller owns `purchaseOrderId` and must hold it across retries.**
 * That is what makes this safe to press twice: the callable's
 * transaction finds the document already there and hands back the number
 * it already has rather than burning a second one. A fresh id per
 * attempt would defeat it and raise two orders for one award, each with
 * its own number read out to a supplier. Generate it once, per
 * AGENTS.md §3, and keep it for the life of the intent.
 *
 * If the callable succeeds and the package write then fails, the order
 * exists and the package does not know. Pressing again converges, which
 * is why the id has to survive the failure — so the recovery is the same
 * action rather than a special one.
 */
export async function raisePurchaseOrderFromAward(
  context: BidContext,
  functions: FunctionsPort,
  pkg: BidPackage,
  bid: Bid,
  purchaseOrderId: string,
): Promise<{ readonly pkg: BidPackage; readonly number: string }> {
  if (pkg.status !== 'awarded') {
    throw new BidError('Award the package first, so the order records a decision.');
  }
  if (pkg.awardedBidId !== bid.id) {
    throw new BidError('That is not the bid this package was awarded to.');
  }
  if (pkg.purchaseOrderId) {
    throw new BidError('A purchase order has already been raised for this package.');
  }

  const { number } = await createPurchaseOrder(functions, {
    companyId: context.companyId,
    jobId: context.jobId,
    purchaseOrderId,
    title: pkg.title,
    // The scope travels, which is the point of raising it from the
    // package: what the trade priced is what they are ordered to do, in
    // the same words. Retyping it is how the two come to differ.
    scope: pkg.scope,
    assignedContactId: bid.contactId,
    sourceBidPackageId: pkg.id,
  });

  return { pkg: await writePackage(context, pkg, { purchaseOrderId }), number };
}

/** What a trade may see of a package, which is everything but the prices. */
export function visiblePackagesFor(all: readonly BidPackage[], contactId: string): BidPackage[] {
  return all.filter(
    (pkg) => isPackageVisibleToTrades(pkg) && pkg.invitedContactIds.includes(contactId),
  );
}
